// Small presentational pieces shared by the Video QC page, styled to match
// the Planner's Discover / Unique Reach pages (navy + gold, Inter, raised
// white surfaces). The tokens mirror --dv2-* in InfluencerDiscoverV2/discoverV2.css.
// Hover, focus and motion live in videoQc.css.
import React, { useEffect, useRef } from 'react';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { STATUS, TONE, VERDICT } from './qcFormat';
import './videoQc.css';

export const C = {
    navy: '#111936', navy700: '#1b2550', gold: '#febd1c', goldSoft: '#fff7e2', goldInk: '#7a5e10',
    text: '#1f2433', body: '#374151', muted: '#5f6677', faint: '#9aa1b2', th: '#5b6478',
    border: '#dfe4ee', line: '#e9eaf0', soft: '#f5f6fa', head: '#fbfbfd',
    red: '#dc2626',
};

export const SURFACE = {
    background: 'linear-gradient(180deg, #ffffff 0%, #f5f8fd 100%)',
    border: `1px solid ${C.border}`,
    borderRadius: 16,
    boxShadow: '0 2px 6px rgba(17, 25, 54, 0.06), 0 22px 48px -22px rgba(17, 25, 54, 0.3)',
};

// Borderless variants keep a transparent 1px border so every variant is the
// same height side by side.
const CLEAR = '1px solid transparent';
const BUTTON = {
    primary: { background: C.gold, color: C.navy, border: CLEAR, boxShadow: '0 4px 12px -5px rgba(254,189,28,0.8)' },
    secondary: { background: '#fff', color: C.text, border: `1px solid ${C.border}` },
    subtle: { background: C.soft, color: C.text, border: `1px solid ${C.line}` },
    navy: { background: C.navy, color: '#fff', border: CLEAR },
    success: { background: '#16a34a', color: '#fff', border: CLEAR },
    // AI actions, as AI Discovery on the Discover page: navy gradient, gold sparkle.
    ai: { background: 'linear-gradient(135deg, #111936 0%, #2b3a78 100%)', color: '#fff', border: CLEAR, boxShadow: '0 6px 14px -8px rgba(17,25,54,0.7)' },
};

export function Button({ variant = 'secondary', small = false, disabled = false, style, className = '', children, ...rest }) {
    return (
        <button
            type="button"
            disabled={disabled}
            className={`vqc-btn vqc-btn--${variant} ${className}`.trim()}
            style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontFamily: 'inherit', boxSizing: 'border-box',
                minHeight: small ? 32 : 36, padding: small ? '6px 12px' : '9px 16px', borderRadius: 9, fontSize: small ? 12 : 13, fontWeight: 600,
                cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1, whiteSpace: 'nowrap',
                ...BUTTON[variant], ...style,
            }}
            {...rest}
        >
            {children}
        </button>
    );
}

// A show/hide toggle, for text that stays folded until asked for (the brief,
// a transcript). The chevron turns when open; the label should say so too.
export const Disclosure = ({ open, controls, onToggle, children }) => (
    <Button aria-expanded={open} aria-controls={controls} onClick={onToggle}>
        {children}
        <ExpandMoreRoundedIcon aria-hidden="true" className="vqc-chevron"
            style={{ fontSize: 18, marginRight: -4, color: C.muted, transform: open ? 'rotate(180deg)' : 'none' }} />
    </Button>
);

export const Pill = ({ bg, color, children, title, style }) => (
    <span title={title} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 9px', background: bg, color, fontSize: 12, fontWeight: 700, borderRadius: 8, whiteSpace: 'nowrap', ...style }}>
        {children}
    </span>
);

export function VerdictBadge({ verdict, large = false }) {
    const v = VERDICT[verdict];
    if (!v) return <span style={{ color: C.muted }}>—</span>;
    if (!large) return <Pill bg={v.bg} color={v.color}>{v.icon} {v.label}</Pill>;
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '7px 16px', background: v.bg, color: v.color, fontSize: 17, fontWeight: 800, borderRadius: 11, border: `1px solid ${v.color}33`, letterSpacing: -0.2 }}>
            {v.icon} {v.label}
        </span>
    );
}

export function StatusBadge({ status, stage }) {
    const s = STATUS[status] || STATUS.pending;
    const running = status === 'queued' || status === 'processing';
    return (
        <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <Pill bg={s.bg} color={s.color}>{running && <Spinner />}{s.label}</Pill>
            {running && stage && stage !== s.label && <span style={{ fontSize: 12, color: C.muted }}>{stage}</span>}
        </div>
    );
}

export const Spinner = ({ size = 10 }) => (
    <span
        aria-hidden="true"
        className="vqc-spin"
        style={{ width: size, height: size, border: '2px solid currentColor', borderRightColor: 'transparent', borderRadius: '50%', display: 'inline-block', flexShrink: 0 }}
    />
);

// Focus moves into the dialog when it opens and back when it closes; Escape closes it.
export function useDialogFocus(onClose) {
    const ref = useRef(null);
    useEffect(() => {
        const previous = document.activeElement;
        if (ref.current) ref.current.focus({ preventScroll: true });
        return () => { if (previous && typeof previous.focus === 'function') previous.focus({ preventScroll: true }); };
    }, []);
    const onKeyDown = (e) => {
        if (e.key !== 'Escape') return;
        e.stopPropagation();
        onClose();
    };
    return { ref, onKeyDown };
}

export function Modal({ title, subtitle, badge, onClose, width = 560, children, footer }) {
    const { ref, onKeyDown } = useDialogFocus(onClose);
    return (
        <div role="dialog" aria-modal="true" aria-label={title} onClick={onClose} onKeyDown={onKeyDown}
            style={{ position: 'fixed', inset: 0, background: 'rgba(17,25,54,0.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 3000, padding: '40px 16px', overflowY: 'auto' }}>
            <div ref={ref} tabIndex={-1} onClick={(e) => e.stopPropagation()}
                style={{ background: '#fff', borderRadius: 18, width: '100%', maxWidth: width, textAlign: 'left', fontFamily: "'Inter', 'Segoe UI', Roboto, system-ui, sans-serif", color: C.text, boxShadow: '0 30px 60px -20px rgba(17,25,54,0.5)', outline: 'none' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, padding: '18px 24px', borderBottom: `1px solid ${C.line}` }}>
                    <div style={{ minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 17, fontWeight: 700, color: C.text }}>{title}{badge}</div>
                        {subtitle && <div style={{ fontSize: 12, color: C.muted, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{subtitle}</div>}
                    </div>
                    <button type="button" aria-label="Close" className="vqc-focus vqc-icon-btn" onClick={onClose}
                        style={{ width: 32, height: 32, margin: '-4px -8px 0 0', flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', fontSize: 22, color: C.muted, cursor: 'pointer', lineHeight: 1, borderRadius: 8 }}>×</button>
                </div>
                <div style={{ padding: 24 }}>{children}</div>
                {footer && <div style={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 10, padding: '14px 24px', borderTop: `1px solid ${C.line}`, background: C.head, borderRadius: '0 0 18px 18px' }}>{footer}</div>}
            </div>
        </div>
    );
}

// A tone's colour as a dot, or a diamond for flagged items.
export const ToneMark = ({ tone, flag, size = 10 }) => (
    <span aria-hidden="true" style={{ width: size, height: size, flexShrink: 0, background: TONE[tone].color, borderRadius: flag ? 2 : size, transform: flag ? 'rotate(45deg)' : undefined }} />
);

// Uppercase label with the gold bar, as .dv2-section-label on Discover.
export const SectionTitle = ({ children, hint, aside }) => (
    <div style={{ marginBottom: 12, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: C.th }}>
                <span style={{ width: 4, height: 14, borderRadius: 4, background: C.gold, display: 'inline-block' }} />
                {children}
            </div>
            {hint && <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{hint}</div>}
        </div>
        {aside}
    </div>
);

// Card header with the navy icon tile, as "Instagram Creators" on Discover.
export const CardHeader = ({ icon, title, subtitle, actions }) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, flex: '1 1 340px' }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: C.navy, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>{icon}</div>
            <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 17, fontWeight: 700, color: C.text }}>{title}</div>
                {subtitle && <div style={{ fontSize: 12.5, color: C.muted, marginTop: 1 }}>{subtitle}</div>}
            </div>
        </div>
        {actions && <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>{actions}</div>}
    </div>
);

export const Empty = ({ icon, title, children, extra }) => (
    <div style={{ textAlign: 'center', padding: '28px 24px', background: C.soft, borderRadius: 14, border: `1px dashed ${C.border}` }}>
        {icon && <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10, fontSize: 32 }}>{icon}</div>}
        <div style={{ fontWeight: 700, color: C.text, fontSize: 15 }}>{title}</div>
        {children && <p style={{ color: C.muted, fontSize: 13, margin: '4px auto 0', maxWidth: 460 }}>{children}</p>}
        {extra}
    </div>
);
